import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './ForMedicalFaculty.css';
import axiosInstance from '../../api/axiosInstance';

const ForMedicalFaculty = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    whatsappNumber: '',
    sameAsMobile: false,
    email: '',
    currentCityState: '',
    registrationDetails: '',
    qualification: '',
    speciality: '',
    currentDesignation: '',
    teachingExperience: '',
    preferredLocation: '',
    joiningAvailability: '',
    additionalInfo: '',
    consent: false,
  });

  const [cvFile, setCvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successBanner, setSuccessBanner] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const qualifications = [
    "MBBS",
    "MD (Doctor of Medicine)",
    "MS (Master of Surgery)",
    "DNB (Diplomate of National Board)",
    "DM (Doctorate of Medicine / Super Speciality)",
    "MCh (Magister Chirurgiae / Super Speciality)",
    "M.Sc (Medical Anatomy / Physiology / Biochemistry / Pharmacology / Microbiology)",
    "Ph.D. / Other Relevant Medical Qualification"
  ];

  const designations = [
    "Tutor / Demonstrator",
    "Senior Resident (SR)",
    "Assistant Professor",
    "Associate Professor",
    "Professor",
    "Professor & Head of Department (HOD)",
    "Medical Superintendent / Dean / Director"
  ];

  const experienceList = [
    "0 – 1 Year (Junior / Senior Resident level)",
    "1 – 3 Years (Senior Resident / Entry level)",
    "3 – 5 Years (Assistant Professor level)",
    "5 – 8 Years (Associate Professor level)",
    "8 – 10 Years (Associate / Professor level)",
    "10+ Years (Senior Professor / HOD / Dean level)"
  ];

  const availabilityOptions = [
    "Immediate / Within 7 Days",
    "15 Days Notice",
    "30 Days (1 Month)",
    "60 Days (2 Months)",
    "90 Days (3 Months)",
    "Exploring for Upcoming Academic Session"
  ];

  // 10-Second Auto-dismiss success notification
  useEffect(() => {
    let timer;
    if (successBanner) {
      timer = setTimeout(() => {
        setSuccessBanner(false);
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [successBanner]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Strict 10-digit mobile filter
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        mobileNumber: digitsOnly,
        whatsappNumber: prev.sameAsMobile ? digitsOnly : prev.whatsappNumber,
      }));
      if (errorMsg) setErrorMsg('');
      return;
    }

    if (name === 'sameAsMobile') {
      setFormData((prev) => ({
        ...prev,
        sameAsMobile: checked,
        whatsappNumber: checked ? prev.mobileNumber : ''
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errorMsg) setErrorMsg('');
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        setErrorMsg("File size exceeds 10MB limit. Please upload a smaller file.");
        return;
      }
      setCvFile(file);
      if (errorMsg) setErrorMsg('');
    }
  };

  const removeFile = () => {
    setCvFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Indian Mobile Number Check (10 digits starting 6, 7, 8, 9)
    const indianMobileRegex = /^[6-9]\d{9}$/;
    if (!indianMobileRegex.test(formData.mobileNumber.trim())) {
      setErrorMsg("Please enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9.");
      return;
    }

    // Email Format Check
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email.trim())) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    if (!cvFile) {
      setErrorMsg("Please upload your updated CV / Resume file (PDF, DOC, DOCX).");
      return;
    }

    if (!formData.consent) {
      setErrorMsg("Please accept the mandatory privacy consent terms.");
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        submitData.append(key, formData[key]);
      });
      submitData.append('cvFile', cvFile);

      const response = await axiosInstance.post('/api/faculty/submit', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.success || response.status === 201 || response.status === 200) {
        setSuccessBanner(true);
        setErrorMsg('');
        
        // Reset form
        setCvFile(null);
        setFormData({
          fullName: '', mobileNumber: '', whatsappNumber: '', sameAsMobile: false,
          email: '', currentCityState: '', registrationDetails: '', qualification: '',
          speciality: '', currentDesignation: '', teachingExperience: '', preferredLocation: '',
          joiningAvailability: '', additionalInfo: '', consent: false
        });
      }
    } catch (err) {
      console.error('Faculty Submit Error:', err);
      const msg = err.response?.data?.message || 'Unable to submit profile. Please check your connection and try again.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>For Medical Faculty | Nutrivio</title>
        <meta
          name="description"
          content="Submit your medical faculty profile to Nutrivio for permanent and full-time academic appointments in medical colleges and teaching hospitals."
        />
      </Helmet>

      <div className="for-medical-faculty-page-wrapper">
        <div className="for-medical-faculty-page-ambient-grid"></div>

        <div className="for-medical-faculty-page-container">
          <div className="for-medical-faculty-page-layout">
            
            {/* =========================================================
                LEFT COLUMN: STRUCTURED MODULAR DIVS (PINNED STICKY)
            ========================================================= */}
            <div className="for-medical-faculty-page-sidebar">
              <div className="for-medical-faculty-page-accent-glow"></div>

              {/* 1. Header Intro Card Div */}
              <div className="for-faculty-card-box for-faculty-intro-card">
                <div className="for-medical-faculty-page-hero-pill">
                  <span className="for-medical-faculty-page-pulse-dot"></span>
                  <span>Explore Academic Opportunities</span>
                </div>

                <h1 className="for-medical-faculty-page-main-heading">
                  Submit Your <br />
                  <span className="for-medical-faculty-page-gradient-text">Professional Profile</span>
                </h1>

                <p className="for-medical-faculty-page-intro-lead">
                  Medical professionals interested in Permanent & Full-Time academic opportunities with Medical Colleges and Teaching Hospitals may submit their professional profiles to Nutrivio.
                </p>

                <p className="for-medical-faculty-page-intro-sub">
                  Profiles are evaluated for suitable institutional appointments based on academic qualification, speciality, recognized teaching experience, and location preference.
                </p>

                {/* Trust Criteria Pills */}
                <div className="for-medical-faculty-page-criteria-strip">
                  <div className="for-medical-faculty-page-criteria-item">
                    <span className="for-medical-faculty-page-tick">✓</span>
                    <span>Permanent & Full-Time Roles</span>
                  </div>
                  <div className="for-medical-faculty-page-criteria-item">
                    <span className="for-medical-faculty-page-tick">✓</span>
                    <span>Department-Wise Matching</span>
                  </div>
                  <div className="for-medical-faculty-page-criteria-item">
                    <span className="for-medical-faculty-page-tick">✓</span>
                    <span>Pan-India Teaching Institutions</span>
                  </div>
                </div>
              </div>

              {/* 2. Institutional Engagement Model Card Div */}
              <div className="for-faculty-card-box for-faculty-benefits-card">
                <div className="for-medical-faculty-page-benefits-header">
                  <h6>Institutional Engagement Model</h6>
                  <span>Verified Appointments</span>
                </div>

                <div className="for-medical-faculty-page-benefit-list">
                  <div className="for-medical-faculty-page-benefit-item">
                    <div className="for-medical-faculty-page-benefit-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                      </svg>
                    </div>
                    <div>
                      <strong>Direct Institutional Appointments</strong>
                      <p>Opportunities across established Medical Colleges, Deemed Universities, and upcoming Teaching Hospitals.</p>
                    </div>
                  </div>

                  <div className="for-medical-faculty-page-benefit-item">
                    <div className="for-medical-faculty-page-benefit-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </div>
                    <div>
                      <strong>Designation & Eligibility Alignment</strong>
                      <p>Review of academic qualifications, recognized teaching experience, and departmental requirements.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Doctor Privacy Guarantee Card Div */}
              <div className="for-faculty-card-box for-faculty-privacy-card">
                <div className="for-medical-faculty-page-privacy-head">
                  <div className="for-medical-faculty-page-lock-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <div>
                    <span className="for-medical-faculty-page-privacy-tag">Strict Confidentiality Protocol</span>
                    <h4>Non-Public Profile Protection</h4>
                  </div>
                </div>
                <p>
                  <strong>Doctor information, CVs and uploaded documents are never publicly visible on the website.</strong> All submissions are securely stored and accessible solely to authorized administrators for institutional coordination.
                </p>
              </div>

              {/* 4. Direct Faculty Desk Quick Channels Div */}
              <div className="for-faculty-quick-channels-box">
                <a href="tel:+919038320103" className="for-faculty-quick-pill">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span>+91 90383 20103</span>
                </a>
                <a href="https://wa.me/919038320103" target="_blank" rel="noopener noreferrer" className="for-faculty-quick-pill whatsapp">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                  </svg>
                  <span>WhatsApp Desk</span>
                </a>
                <a href="mailto:nutrivioheadoffice@gmail.com" className="for-faculty-quick-pill">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <span>Faculty Email</span>
                </a>
              </div>

            </div>

            {/* =========================================================
                RIGHT COLUMN: FORM CARD (SLIDES SMOOTHLY)
            ========================================================= */}
            <div className="for-medical-faculty-page-form-col">
              <div className="for-medical-faculty-page-form-card">
                
                <div className="for-medical-faculty-page-form-header">
                  <div className="for-medical-faculty-page-form-title-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <line x1="20" y1="8" x2="20" y2="14"/>
                      <line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="for-medical-faculty-page-card-title">Faculty Registration Form</h3>
                    <p className="for-medical-faculty-page-card-sub">Please fill in your academic profile and credentials accurately.</p>
                  </div>
                </div>

                {/* 10-Second Auto-dismissible One-Line Success Banner */}
                {successBanner && (
                  <div className="faculty-one-line-success-banner" role="alert">
                    <div className="faculty-banner-content-left">
                      <span className="faculty-banner-check">✓</span>
                      <span className="faculty-banner-text">
                        Profile submitted successfully! Our faculty advisory desk will connect with you when a matching role arises.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="faculty-banner-close-btn"
                      onClick={() => setSuccessBanner(false)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Error Banner */}
                {errorMsg && (
                  <div className="faculty-one-line-error-banner" role="alert">
                    <div className="faculty-banner-content-left">
                      <span>⚠️</span>
                      <span className="faculty-banner-text">{errorMsg}</span>
                    </div>
                    <button
                      type="button"
                      className="faculty-banner-close-btn error"
                      onClick={() => setErrorMsg('')}
                      aria-label="Close error"
                    >
                      ✕
                    </button>
                  </div>
                )}

                <form className="for-medical-faculty-page-form" onSubmit={handleSubmit}>
                  
                  {/* Field: Full Name */}
                  <div className="for-medical-faculty-page-group">
                    <label className="for-medical-faculty-page-label">
                      Full Name <span className="for-medical-faculty-page-req">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="fullName"
                      className="for-medical-faculty-page-input"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Field Row: Mobile Number & WhatsApp Number */}
                  <div className="for-medical-faculty-page-row">
                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Mobile Number <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <input 
                        type="tel" 
                        name="mobileNumber"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter 10-digit mobile number"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        maxLength={10}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="for-medical-faculty-page-group">
                      <div className="for-medical-faculty-page-label-split">
                        <label className="for-medical-faculty-page-label">WhatsApp Number</label>
                        <label className="for-medical-faculty-page-checkbox-sublabel">
                          <input 
                            type="checkbox"
                            name="sameAsMobile"
                            checked={formData.sameAsMobile}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <span>Same as Mobile</span>
                        </label>
                      </div>
                      <input 
                        type="tel" 
                        name="whatsappNumber"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter WhatsApp number"
                        value={formData.whatsappNumber}
                        onChange={handleInputChange}
                        disabled={formData.sameAsMobile || loading}
                      />
                    </div>
                  </div>

                  {/* Field Row: Email & Current City & State */}
                  <div className="for-medical-faculty-page-row">
                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Official / Personal Email <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <input 
                        type="email" 
                        name="email"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Current City & State <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="currentCityState"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter current city and state"
                        value={formData.currentCityState}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field: Medical Registration Details */}
                  <div className="for-medical-faculty-page-group">
                    <label className="for-medical-faculty-page-label">
                      Medical Council Registration Details <span className="for-medical-faculty-page-req">*</span>
                    </label>
                    <input 
                      type="text" 
                      name="registrationDetails"
                      className="for-medical-faculty-page-input"
                      placeholder="Enter council registration details (e.g. State Council Reg No.)"
                      value={formData.registrationDetails}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>

                  {/* Field Row: Qualification & Speciality / Department */}
                  <div className="for-medical-faculty-page-row">
                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Highest Qualification <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <select 
                        name="qualification"
                        className="for-medical-faculty-page-select"
                        value={formData.qualification}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select qualification</option>
                        {qualifications.map((q, i) => (
                          <option key={i} value={q}>{q}</option>
                        ))}
                      </select>
                    </div>

                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Speciality / Department <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="speciality"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter speciality or department"
                        value={formData.speciality}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Field Row: Current Designation & Teaching Experience */}
                  <div className="for-medical-faculty-page-row">
                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Current Academic Designation <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <select 
                        name="currentDesignation"
                        className="for-medical-faculty-page-select"
                        value={formData.currentDesignation}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select current designation</option>
                        {designations.map((d, i) => (
                          <option key={i} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>

                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Teaching Experience <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <select 
                        name="teachingExperience"
                        className="for-medical-faculty-page-select"
                        value={formData.teachingExperience}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select experience level</option>
                        {experienceList.map((exp, i) => (
                          <option key={i} value={exp}>{exp}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Field Row: Preferred Location & Joining Availability */}
                  <div className="for-medical-faculty-page-row">
                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Preferred Location / States <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <input 
                        type="text" 
                        name="preferredLocation"
                        className="for-medical-faculty-page-input"
                        placeholder="Enter preferred location / states"
                        value={formData.preferredLocation}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="for-medical-faculty-page-group">
                      <label className="for-medical-faculty-page-label">
                        Joining Availability <span className="for-medical-faculty-page-req">*</span>
                      </label>
                      <select 
                        name="joiningAvailability"
                        className="for-medical-faculty-page-select"
                        value={formData.joiningAvailability}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select joining availability</option>
                        {availabilityOptions.map((avail, i) => (
                          <option key={i} value={avail}>{avail}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Field: Upload CV */}
                  <div className="for-medical-faculty-page-group">
                    <label className="for-medical-faculty-page-label">
                      Upload Updated CV / Resume <span className="for-medical-faculty-page-req">*</span>
                    </label>
                    <div className="for-medical-faculty-page-file-dropzone">
                      <input 
                        type="file" 
                        id="faculty-cv-upload"
                        className="for-medical-faculty-page-file-input"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        disabled={loading}
                      />
                      {!cvFile ? (
                        <label htmlFor="faculty-cv-upload" className="for-medical-faculty-page-file-custom">
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                          <div className="for-medical-faculty-page-file-text">
                            <strong>Click to Upload CV / Resume</strong>
                            <span>Supports PDF, DOC, DOCX (Max 10MB)</span>
                          </div>
                        </label>
                      ) : (
                        <div className="for-medical-faculty-page-file-selected">
                          <div className="for-medical-faculty-page-file-info">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                            </svg>
                            <span>{cvFile.name} ({(cvFile.size / (1024 * 1024)).toFixed(2)} MB)</span>
                          </div>
                          <button type="button" className="for-medical-faculty-page-file-remove" onClick={removeFile} disabled={loading}>
                            ✕ Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Field: Additional Information */}
                  <div className="for-medical-faculty-page-group">
                    <label className="for-medical-faculty-page-label">Additional Academic / Professional Details</label>
                    <textarea 
                      name="additionalInfo" 
                      rows="3"
                      className="for-medical-faculty-page-textarea"
                      placeholder="Write additional details here..."
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      disabled={loading}
                    ></textarea>
                  </div>

                  {/* Field: Mandatory Legal Consent */}
                  <div className="for-medical-faculty-page-consent-group">
                    <label className="for-medical-faculty-page-checkbox-label">
                      <input 
                        type="checkbox" 
                        name="consent"
                        className="for-medical-faculty-page-checkbox"
                        checked={formData.consent}
                        onChange={handleInputChange}
                        required
                        disabled={loading}
                      />
                      <span className="for-medical-faculty-page-consent-text">
                        I consent to Nutrivio Medical Services Private Limited collecting and processing the professional information submitted by me for recruitment-related communication and consideration for suitable institutional opportunities, subject to the applicable Privacy Policy.
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="for-medical-faculty-page-submit-btn" disabled={loading}>
                    {loading ? (
                      <span>TRANSMITTING PROFILE...</span>
                    ) : (
                      <>
                        <span>SUBMIT PROFESSIONAL PROFILE</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="for-medical-faculty-page-footer-note">
                    🔒 Doctor information, CVs and uploaded documents are securely stored and never publicly visible.
                  </p>

                </form>

              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ForMedicalFaculty;